import React, { useState, useCallback } from 'react';
import { vendorService } from '../../../services/api';
import { FileSpreadsheet, Download, AlertCircle, X } from 'lucide-react';

const getPageNumbers = (currentPage, totalPages) => {
  const pages = [];
  const maxVisiblePages = 5;

  if (totalPages <= maxVisiblePages) {
    for (let i = 0; i < totalPages; i++) {
      pages.push(i);
    }
  } else {
    pages.push(0);

    let start = Math.max(1, currentPage - 1);
    let end = Math.min(totalPages - 2, currentPage + 1);

    if (currentPage <= 1) {
      end = 3;
    } else if (currentPage >= totalPages - 2) {
      start = totalPages - 4;
    }

    if (start > 1) {
      pages.push('ellipsis1');
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (end < totalPages - 2) {
      pages.push('ellipsis2');
    }

    pages.push(totalPages - 1);
  }
  return pages;
};

const ExcelImportTab = ({ toast }) => {
  // Excel Import States
  const [excelFile, setExcelFile] = useState(null);
  const [excelFileLoading, setExcelFileLoading] = useState(false);
  const [excelSuccess, setExcelSuccess] = useState(false);
  const [excelImportCount, setExcelImportCount] = useState(0);
  const [excelErrors, setExcelErrors] = useState([]);
  const [excelErrorPage, setExcelErrorPageState] = useState(() => {
    try {
      const saved = sessionStorage.getItem('admin_page_excel_import');
      return saved ? Math.max(0, parseInt(saved, 10)) : 0;
    } catch (e) {
      return 0;
    }
  });

  const setExcelErrorPage = useCallback((valOrFn) => {
    setExcelErrorPageState(prev => {
      const next = typeof valOrFn === 'function' ? valOrFn(prev) : valOrFn;
      try {
        sessionStorage.setItem('admin_page_excel_import', String(next));
      } catch (e) {}
      return next;
    });
  }, []);

  const [excelErrorPageSize] = useState(10);

  const handleExcelUpload = async (e) => {
    e.preventDefault();
    if (!excelFile) {
      toast.warning("Please select an Excel file to upload.");
      return;
    }
    setExcelFileLoading(true);
    setExcelErrors([]);
    setExcelSuccess(false);
    setExcelImportCount(0);

    const formData = new FormData();
    formData.append("file", excelFile);

    try {
      const res = await vendorService.uploadExcelListings(formData);
      if (res.success && res.data) {
        setExcelSuccess(true);
        setExcelImportCount(res.data.count || 0);
        setExcelFile(null);
        const fileInput = document.getElementById("excel-file-input");
        if (fileInput) fileInput.value = "";
        toast.success(res.data.message || "Spreadsheet imported successfully!");
      } else {
        if (res.data && res.data.errors) {
          setExcelErrors(res.data.errors);
          toast.error("Validation failed! Please fix the errors highlighted in the table.");
        } else {
          toast.error(res.error || "Failed to upload Excel sheet.");
        }
      }
    } catch (err) {
      toast.error("An error occurred during file upload.");
      console.error(err);
    } finally {
      setExcelFileLoading(false);
    }
  };

  return (
    <div className="space-y-6 text-left">
      {/* Import Card */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="p-6 border-b border-gray-100 bg-white flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-gray-800">Bulk Import Directory Listings</h2>
            <p className="text-xs text-gray-400 mt-1 font-semibold">Upload an Excel spreadsheet to bulk create pre-approved unclaimed listings.</p>
          </div>
          <a
            href="/sample_listings_template.xlsx"
            download="sample_listings_template.xlsx"
            className="inline-flex items-center gap-1.5 bg-[#2ab391] hover:bg-[#23977a] text-white text-xs font-bold px-4 py-2.5 rounded-xl shadow-xs transition-all select-none cursor-pointer active:scale-95 whitespace-nowrap self-start sm:self-auto"
          >
            <Download className="w-3.5 h-3.5" /> Click to download excel format
          </a>
        </div>

        <div className="p-6 space-y-6">
          {/* Success Alert */}
          {excelSuccess && (
            <div className="bg-emerald-50/60 border border-emerald-100 rounded-2xl p-5 flex items-start gap-3.5 select-none animate-in fade-in slide-in-from-top-2 duration-200">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center border border-emerald-200 flex-shrink-0">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <h4 className="font-extrabold text-sm text-emerald-950">Bulk Import Successful!</h4>
                <p className="text-xs text-emerald-750 font-semibold mt-1">
                  Successfully parsed and imported <span className="font-bold text-emerald-900">{excelImportCount}</span> business listings into the database directory.
                </p>
              </div>
            </div>
          )}

          {/* Upload Form */}
          <form onSubmit={handleExcelUpload} className="space-y-4">
            <input
              type="file"
              id="excel-file-input"
              accept=".xlsx, .xls"
              className="hidden"
              onChange={(e) => {
                if (e.target.files && e.target.files[0]) {
                  setExcelFile(e.target.files[0]);
                  setExcelSuccess(false);
                  setExcelErrors([]);
                }
              }}
            />
            <label
              htmlFor="excel-file-input"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                if (e.dataTransfer.files && e.dataTransfer.files[0]) {
                  const file = e.dataTransfer.files[0];
                  if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
                    setExcelFile(file);
                    setExcelSuccess(false);
                    setExcelErrors([]);
                  } else {
                    toast.error("Invalid file format. Please upload an Excel sheet (.xlsx or .xls).");
                  }
                }
              }}
              className={`border-2 border-dashed rounded-2xl p-8 text-center transition-all duration-200 bg-gray-55/30 cursor-pointer block ${excelFile
                ? 'border-[#2ab391] bg-teal-50/10'
                : 'border-gray-200 hover:border-gray-300'
                }`}
            >
              <div className="flex flex-col items-center justify-center gap-3">
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center border ${excelFile
                  ? 'bg-teal-50 border-teal-100 text-[#2ab391]'
                  : 'bg-white border-gray-150 text-slate-450'
                  }`}>
                  <FileSpreadsheet className="w-7 h-7" />
                </div>

                {excelFile ? (
                  <div className="space-y-1">
                    <p className="text-sm font-bold text-gray-800">{excelFile.name}</p>
                    <p className="text-xs text-gray-400 font-semibold">
                      {(excelFile.size / 1024).toFixed(1)} KB
                    </p>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setExcelFile(null);
                        const input = document.getElementById("excel-file-input");
                        if (input) input.value = "";
                      }}
                      className="text-xs font-bold text-rose-600 hover:text-rose-700 transition-colors mt-2 inline-flex items-center gap-1 cursor-pointer bg-white px-3 py-1 rounded-lg border border-rose-100 shadow-2xs hover:bg-rose-50"
                    >
                      <X className="w-3.5 h-3.5" /> Remove file
                    </button>
                  </div>
                ) : (
                  <div className="space-y-1">
                    <p className="text-sm font-bold text-gray-700">
                      Drag and drop your spreadsheet here, or{" "}
                      <span className="text-[#2ab391] hover:underline cursor-pointer font-bold">
                        browse
                      </span>
                    </p>
                    <p className="text-xs text-gray-400 font-medium">Supports Excel sheets (.xlsx, .xls) up to 10MB</p>
                  </div>
                )}
              </div>
            </label>

            <div className="flex justify-end pt-2">
              <button
                type="submit"
                disabled={!excelFile || excelFileLoading}
                className={`px-5 py-2.5 rounded-xl text-sm font-bold active:scale-[0.98] transition-all flex items-center gap-1.5 cursor-pointer shadow-md shadow-[#2ab391]/10 ${!excelFile || excelFileLoading
                  ? 'bg-gray-100 border border-gray-200 text-gray-400 cursor-not-allowed shadow-none active:scale-100'
                  : 'bg-[#2ab391] hover:bg-[#23977a] text-white border-transparent'
                  }`}
              >
                {excelFileLoading ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                    Importing listings...
                  </>
                ) : (
                  "Upload & Validate Sheet"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Validation Errors Table */}
      {excelErrors.length > 0 && (() => {
        const totalErrorPages = Math.ceil(excelErrors.length / excelErrorPageSize);
        const safeErrorPage = Math.min(excelErrorPage, Math.max(0, totalErrorPages - 1));
        const displayedErrors = excelErrors.slice(safeErrorPage * excelErrorPageSize, (safeErrorPage + 1) * excelErrorPageSize);

        return (
          <div className="bg-white rounded-2xl border border-rose-100 shadow-sm overflow-hidden p-6 animate-in fade-in duration-200">
            <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-rose-500" />
                <h3 className="text-base font-bold text-gray-800">Validation Errors Detected ({excelErrors.length})</h3>
              </div>
              <button
                type="button"
                onClick={() => {
                  if (!excelErrors || excelErrors.length === 0) return;
                  const csvRows = [["Excel Row", "Column Name", "Error Message"]];
                  excelErrors.forEach(err => {
                    csvRows.push([`Row ${err.row}`, err.column, err.message]);
                  });
                  const csvContent = csvRows.map(e => e.map(val => `"${String(val).replace(/"/g, '""')}"`).join(",")).join("\n");
                  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                  const url = URL.createObjectURL(blob);
                  const link = document.createElement("a");
                  link.setAttribute("href", url);
                  link.setAttribute("download", `validation_errors_${new Date().toISOString().slice(0, 10)}.csv`);
                  link.style.visibility = 'hidden';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
                className="inline-flex items-center gap-1.5 bg-rose-500 hover:bg-rose-600 text-white text-xs font-bold px-3 py-1.5 rounded-xl shadow-xs transition-all select-none cursor-pointer active:scale-95 whitespace-nowrap"
              >
                <Download className="w-3.5 h-3.5" /> Download Errors (CSV)
              </button>
            </div>
            <p className="text-xs text-rose-600 font-semibold mb-4 bg-rose-50 border border-rose-100 rounded-xl p-3">
              The entire import transaction was rolled back. None of the listings were saved. Please resolve all formatting errors in your file and try uploading again.
            </p>

            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider text-left">
                    <th className="p-3 pl-4">Excel Row</th>
                    <th className="p-3">Column Name</th>
                    <th className="p-3 pr-4">Error Message</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 text-xs font-semibold text-gray-700">
                  {displayedErrors.map((err, idx) => (
                    <tr key={idx} className="hover:bg-gray-50/50 transition-all">
                      <td className="p-3 pl-4 font-bold text-gray-900">Row {err.row}</td>
                      <td className="p-3">
                        <span className="px-2 py-0.5 bg-gray-100 border border-gray-200 rounded font-semibold text-[10px] uppercase text-gray-650">
                          {err.column}
                        </span>
                      </td>
                      <td className="p-3 pr-4 text-rose-600 font-bold">{err.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination footer */}
            {totalErrorPages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-between border-t border-gray-100 pt-5 mt-4 text-xs font-semibold gap-4 select-none">
                <span className="text-gray-500">
                  Showing {safeErrorPage * excelErrorPageSize + 1} to {Math.min((safeErrorPage + 1) * excelErrorPageSize, excelErrors.length)} of {excelErrors.length} errors
                </span>
                <div className="flex gap-1.5 items-center">
                  <button
                    type="button"
                    disabled={safeErrorPage === 0}
                    onClick={() => setExcelErrorPage(p => Math.max(0, p - 1))}
                    className="px-3 py-1.5 border border-gray-200 rounded-xl bg-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-all cursor-pointer font-bold shadow-2xs text-gray-700"
                  >
                    Previous
                  </button>
                  <div className="flex items-center gap-1">
                    {getPageNumbers(safeErrorPage, totalErrorPages).map((p, idx) => {
                      if (p === 'ellipsis1' || p === 'ellipsis2') {
                        return <span key={idx} className="px-2 text-gray-400 font-bold">...</span>;
                      }
                      return (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => setExcelErrorPage(p)}
                          className={`w-8 h-8 flex items-center justify-center rounded-xl border text-xs font-bold ${safeErrorPage === p
                            ? 'bg-[#2ab391] border-transparent text-white shadow-sm'
                            : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                            } transition-all cursor-pointer`}
                        >
                          {p + 1}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    type="button"
                    disabled={safeErrorPage >= totalErrorPages - 1}
                    onClick={() => setExcelErrorPage(p => Math.min(totalErrorPages - 1, p + 1))}
                    className="px-3 py-1.5 border border-gray-200 rounded-xl bg-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-all cursor-pointer font-bold shadow-2xs text-gray-700"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
};

export default ExcelImportTab;
